import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ModalService } from 'src/app/shared/services/modal.service';
import {
  HamburgerRecentListActions,
  OverlayIcons,
} from 'src/app/shared/utility/svg-constants';
import {
  ActionIcons,
  keyStrokes,
  ModalPopupMessages,
  StatusCodes,
} from 'src/app/shared/utility/constants';
import { OverlayButtons } from '../../search.config';
import { RecentSavedSearchPopupComponent } from '../recent-saved-search-popup/recent-saved-search-popup.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { SearchService } from '../../search.service';

@Component({
  selector: 'app-search-options-overlay',
  templateUrl: './search-options-overlay.component.html',
  styleUrl: './search-options-overlay.component.scss',
})
export class SearchOptionsOverlayComponent implements OnInit, OnDestroy {
  @Input() isFrom!: string;
  @Input() focusFirstItem = false;
  @Output() hyperlinkClicked = new EventEmitter<boolean>();
  @ViewChildren('navItem', { read: ElementRef })
  private readonly navItems!: QueryList<ElementRef<HTMLElement>>;

  public overlayIcons = OverlayIcons;
  public overlayButtons = OverlayButtons;
  public recentSearchList: any;
  public requiredRecentSearches: any;
  public pinnedSearchList: any;
  public savedSearchTableActionIcons = JSON.parse(
    JSON.stringify(HamburgerRecentListActions)
  );
  public searchFieldValue!: string;
  public recentListPinIcon = ActionIcons.hamburgerRecentListPin;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public searchService: SearchService,
    private readonly modalService: ModalService,
    private readonly errorHandlerService: ErrorHandlerService
  ) {}

  ngOnInit(): void {
    this.getRecentAndPinnedSearches();
    this.getDataForButtonEnableDisable();
    this.loadRecentSearchBasedOnAction();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public openRecentAndSavedSearchPopup(isSavedSearch: boolean) {
    const dataForPopup = {
      isFromSavedPopup: isSavedSearch,
      tableData: !isSavedSearch ? this.recentSearchList : [],
      isFrom: this.isFrom,
    };
    this.modalService.openPopup(
      dataForPopup,
      isSavedSearch
        ? ModalPopupMessages.savedSearchesHeader
        : ModalPopupMessages.recentSearchesHeader,
      RecentSavedSearchPopupComponent,
      '900px'
    );
  }

  public onhyperlinkClicked(clickedItem: any) {
    this.hyperlinkClicked.emit(true);
    this.searchService.setClickedHyperlinkData({
      item: clickedItem,
      isOpenOverlay: false,
    });
  }

  // SPEC-BUX-001 FR-BUX-011/012: arrow / Home / End move focus through the
  // recent items, "View more", pinned items and the two buttons, in that order.
  @HostListener('keydown', ['$event'])
  public onMenuKeydown(event: KeyboardEvent): void {
    const items = this.getFocusableNavItems();
    if (!items.length) {
      return;
    }
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;
    switch (event.key) {
      case keyStrokes.arrowDown:
        nextIndex = Math.min(currentIndex + 1, items.length - 1);
        break;
      case keyStrokes.arrowUp:
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case keyStrokes.home:
        nextIndex = 0;
        break;
      case keyStrokes.end:
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    items[nextIndex].focus();
  }

  private getFocusableNavItems(): HTMLElement[] {
    return (this.navItems?.toArray() ?? [])
      .map(item => item.nativeElement)
      .filter(element => !(element as HTMLButtonElement).disabled);
  }

  private focusFirstNavItem(): void {
    if (!this.focusFirstItem) {
      return;
    }
    this.focusFirstItem = false;
    // Wait for the freshly loaded lists to render before focusing.
    setTimeout(() => this.getFocusableNavItems()[0]?.focus());
  }

  public onActionClicked(clickedIcon: any, rowData: any) {
    if (clickedIcon.name === ActionIcons.hamburgerRecentListPin) {
      this.searchService.preparePayloadForPinOrUnpin(rowData, this.isFrom);
    } else if (clickedIcon.name === ActionIcons.hamburgerRecentListSave) {
      this.searchService.openSaveAsNewSearchPopup(
        false,
        this.isFrom,
        '',
        rowData
      );
    } else if (clickedIcon.name === ActionIcons.hamburgerRecentListDelete) {
      this.searchService.deleteSearchPopup(rowData, this.isFrom, true);
    }
  }

  private getRecentAndPinnedSearches(isReload?: boolean) {
    this.searchService
      .getRecentAndPinnedSearchList({ screenTag: this.isFrom })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          if (result.statusCode == StatusCodes.Success) {
            this.recentSearchList = result.data?.isRecent;
            this.requiredRecentSearches = this.recentSearchList.slice(0, 10);
            this.pinnedSearchList = result.data?.isPinned;
            if (isReload) {
              this.searchService.setRecentGridReloadData(this.recentSearchList);
            }
          } else if (result.statusCode == StatusCodes.SuccessfulRequest) {
            this.recentSearchList = [];
            this.requiredRecentSearches = [];
            if (isReload) {
              this.searchService.setRecentGridReloadData(this.recentSearchList);
            }
          } else {
            this.errorHandlerService.handleApiError(result);
          }
          this.focusFirstNavItem();
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
          this.focusFirstNavItem();
        },
      });
  }

  private getDataForButtonEnableDisable() {
    this.searchService.isHamburgerSaveNewButtonData
      .pipe(takeUntil(this.destroy$))
      .subscribe(fieldData => {
        this.searchFieldValue = fieldData;
      });
  }

  private loadRecentSearchBasedOnAction() {
    this.searchService.isActionSuccessful
      .pipe(takeUntil(this.destroy$))
      .subscribe(action => {
        if (action) {
          this.getRecentAndPinnedSearches(true);
          this.searchService.setActionSuccessfulData(false);
        }
      });
  }
}
